/**
 * Asset export configuration
 *
 * USAGE:
 * ```typescript
 * const config = useMemo(() =>
 *   createAssetExportConfig(sourceOptions),
 *   [sourceOptions]
 * );
 *
 * <UniversalExportModal config={config} />
 * ```
 */

import type {
  ExportFilterSection,
  UniversalExportConfig,
} from '@/components/export/universal/types';
import { GraphQueryBuilder } from '@/components/export/universal/utils';
import type { Asset, Filter, Operator } from '@/types';
import { ASSET_STATUS_OPTIONS } from '@/utils/mappings.util';

/**
 * Interface for source options (from integrations)
 * Supports both flat options and grouped options (e.g., Chariot group)
 */
export interface SourceOption {
  readonly label: string;
  readonly value: string;
  readonly isGroup?: boolean;
  readonly items?: readonly SourceOption[];
}

/**
 * Column definitions - 16 columns total
 * Based on ExportAssets.tsx lines 21-44
 * Added: attack_surface, tags, country
 */
const ASSET_COLUMNS = [
  { id: 'asset_name', label: 'Asset Name', defaultSelected: true },
  { id: 'asset_identifier', label: 'Asset Identifier', defaultSelected: true },
  { id: 'class', label: 'Class', defaultSelected: true },
  { id: 'status', label: 'Status', defaultSelected: true },
  { id: 'source', label: 'Source', defaultSelected: true },
  { id: 'origination', label: 'Origin', defaultSelected: true },
  { id: 'parent', label: 'Parent', defaultSelected: true },
  { id: 'attack_surface', label: 'Attack Surface', defaultSelected: true },
  { id: 'tags', label: 'Tags', defaultSelected: true },
  { id: 'country', label: 'Country', defaultSelected: true },
  { id: 'as_name', label: 'AS Name', defaultSelected: true },
  { id: 'as_number', label: 'AS Number', defaultSelected: true },
  { id: 'first_seen', label: 'First Seen', defaultSelected: true },
  { id: 'last_seen', label: 'Last Seen', defaultSelected: true },
  { id: 'history', label: 'History', defaultSelected: true },
  { id: 'notes', label: 'Notes', defaultSelected: true },
] as const;

/**
 * Extract class values from source options
 * Handles both grouped options (e.g., Chariot with items) and flat options
 *
 * @param options - Selected source options (may include groups)
 * @returns Array of class values for relationship query
 */
function extractClassValues(
  options: readonly Readonly<{
    value: string;
    isGroup?: boolean;
    items?: readonly { value: string }[];
  }>[]
): string[] {
  return options
    .flatMap(option => {
      if (option.isGroup && option.items) {
        return option.items.map(item => item.value);
      }
      return option.value;
    })
    .filter(value => value && typeof value === 'string' && value.trim() !== '');
}

/**
 * Base asset export configuration (without source filter)
 * Source filter is injected dynamically via factory function
 */
export const assetExportConfigBase = {
  entityType: 'Asset',
  apiEntityType: 'asset' as const,
  storageKey: 'ADVANCE_EXPORT_OPTIONS.assets.v2',

  filterSections: [
    {
      id: 'status',
      label: 'Status',
      options: ASSET_STATUS_OPTIONS.map(({ label, value }) => ({
        label,
        value,
      })),
      defaultSelected: [{ label: 'Active', value: 'A' }], // Default: Active only
      showWhenSelected: false,
    },
  ],

  columns: [...ASSET_COLUMNS],

  formats: [
    { label: 'CSV', value: 'csv' },
    { label: 'JSON', value: 'json' },
  ],
  defaultFormat: { label: 'CSV', value: 'csv' },

  buildQuery: (
    filters: Readonly<
      Record<
        string,
        readonly Readonly<{
          value: string;
          isGroup?: boolean;
          items?: readonly { value: string }[];
        }>[]
      >
    >,
    _selectedRows: readonly string[]
  ) => {
    let builder = GraphQueryBuilder.create(['Asset']);

    if (filters.status && filters.status.length > 0) {
      const statusValues = filters.status.map(s => s.value);

      if (statusValues.length === 1) {
        builder = builder.filter('status', '=', statusValues[0]);
      } else if (statusValues.length > 0) {
        const statusFilters: readonly Filter[] = statusValues.map(value => ({
          field: 'status',
          operator: '=' as Operator,
          value,
        }));
        builder = builder.orFilters(statusFilters);
      }
    }

    if (filters.source && filters.source.length > 0) {
      const originValues = extractClassValues(filters.source);

      if (originValues.length > 0) {
        builder = builder.filters([
          {
            field: '',
            operator: 'OR',
            value: originValues.map(origin => ({
              field: 'origins',
              operator: 'IN',
              value: origin,
              reverseOperands: true,
            })),
          },
        ] as Filter[]);
      }
    }

    return builder.orderBy('name', false).build();
  },

  validateExport: (
    _filters: Readonly<Record<string, readonly Readonly<unknown>[]>>,
    columns: readonly Readonly<unknown>[],
    _selectedRows: readonly string[]
  ) => {
    if (columns.length === 0) {
      return [
        {
          field: 'columns' as const,
          message: 'Please select at least one column to export.',
        },
      ];
    }
    return null;
  },
} as const;

/**
 * Factory function for creating complete asset config with dynamic source filter
 *
 * This function is called in the parent component with the current sourceOptions
 * to inject the dynamic source filter section.
 *
 * @param sourceOptions - Available source options from integrations (built in parent component)
 * @returns Complete asset export config with dynamic source filter
 *
 * @example
 * ```typescript
 * // In parent component:
 * const sourceOptions = useMemo(() => {
 *   // Build source options from integrations
 *   return [
 *     { label: 'Chariot', value: 'chariot', isGroup: true, items: [...] },
 *     { label: 'GitHub', value: 'github' },
 *     // ...
 *   ];
 * }, [integrations]);
 *
 * const assetExportConfig = useMemo(() => {
 *   return createAssetExportConfig(sourceOptions);
 * }, [sourceOptions]);
 * ```
 */
export function createAssetExportConfig(
  sourceOptions: readonly SourceOption[]
): UniversalExportConfig<Asset> {
  // Create source filter section from dynamic options
  const sourceFilterSection: ExportFilterSection = {
    id: 'source',
    label: 'Origin',
    options: sourceOptions as readonly Readonly<{
      label: string;
      value: string;
    }>[],
    defaultSelected: [],
    showWhenSelected: false,
  };

  return {
    ...assetExportConfigBase,
    filterSections: [
      ...assetExportConfigBase.filterSections,
      sourceFilterSection,
    ],
  } as UniversalExportConfig<Asset>;
}
